/**
 * External-milestones API client.
 *
 * IMPORTANT: this module DOES NOT route through services/api/client.ts. Doing
 * so would clear localStorage.monksheets_auth_token AND dispatch the global
 * `auth:expired` window event on ANY 401 (see client.ts:27-31), which
 * navigates the WHOLE app — including the external surface — to the internal
 * Google /login page. That is exactly the bug this module exists to prevent
 * (external-board-redirect-login debug session, 2026-07-02): KanbanSwimlane
 * previously called the internal `fetchMilestones` (services/api/milestones.ts)
 * unconditionally for both audiences, so any 401 on the milestones list
 * (expired external session, revoked invite, transient auth hiccup, …) kicked
 * every external viewer out to /login instead of surfacing an inline error.
 *
 * We use fetch() directly with credentials:'include' so the HttpOnly external
 * session cookie is sent, but no internal token/auth state is ever touched.
 *
 * Phase 23 Landmine #8 — same isolation contract as externalBoard.ts /
 * externalProjects.ts / externalAuth.ts.
 */
import { ExternalApiError } from './externalAuth';
import type { Milestone } from './milestones';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

async function externalFetch<T>(method: string, path: string): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    type ErrorPayload = { error?: string; message?: string };
    const payload = (await res.json().catch(() => ({}))) as ErrorPayload;
    throw new ExternalApiError({
      statusCode: res.status,
      errorCode: typeof payload.error === 'string' ? payload.error : undefined,
      message:
        typeof payload.message === 'string'
          ? payload.message
          : (payload.error ?? 'Unknown error'),
    });
  }
  return (await res.json()) as T;
}

export async function fetchMilestonesExternal(projectId: string): Promise<Milestone[]> {
  const res = await externalFetch<{ data: Milestone[] }>(
    'GET',
    `/projects/${projectId}/milestones`,
  );
  return res.data;
}
